import { store } from 'config/redux'
import { strings } from 'context/LanguageContext/strings'
import { alcoholUtility } from 'utils/alcoholutils'

export const productUtility = {
    canAddItem: product => {
        const { configuration } = store.getState()

        const allowedBasketQty = configuration.get('basket_max_items_qty')
        const allowedBasketAmount = configuration.get('basket_order_limit')
        const allowedUniqueBasketQty = configuration.get('basket_unique_items_limit')

        const { itemAvailable, error } = productUtility.isProductAvailable(product)
        // If item is available, continue, else return
        if (!itemAvailable) {
            return { canAdd: false, error }
        }
        if (!productUtility.isAgeRestrictedProductAllowed(product)) {
            return { canAdd: false, error: strings.itemUnavailable }
        }

        if (productUtility.maxBasketQtyReached(allowedBasketQty)) {
            return { canAdd: false, error: strings.maxQtyAdded, isGlobal: true }
        }

        if (productUtility.maxBasketAmountReached(product, allowedBasketAmount)) {
            return { canAdd: false, error: strings.maxBasketAmtAdded, isGlobal: true }
        }

        if (productUtility.maxBasketUniqueQuantityReached(product, allowedUniqueBasketQty)) {
            return { canAdd: false, error: strings.maxQtyAdded, isGlobal: true }
        }

        if (alcoholUtility.isAlcoholRestricted(product)) {
            return { canAdd: false, error: strings.itemUnavailable }
        }

        if (alcoholUtility.isRestrictedTime(product)) {
            return { canAdd: false, error: strings.itemUnavailable }
        }

        return { canAdd: true, error: '' }
    },

    maxBasketQtyReached: allowedBasketQty => {
        const { basketCount } = productUtility.getBasketTotals()
        return basketCount >= allowedBasketQty
    },

    maxBasketAmountReached: (product, allowedBasketAmount) => {
        const { basketAmount } = productUtility.getBasketTotals()
        return basketAmount + product.price >= allowedBasketAmount
    },

    maxBasketUniqueQuantityReached: (product, allowedUniqueBasketQty) => {
        const { totalUniqueIds } = productUtility.getBasketTotals()
        const matchingIDs = new Set()

        if (product && product.matching_ids && product.matching_ids.length > 0) {
            product.matching_ids.forEach(id => {
                matchingIDs.add(id)
            })
        }

        return totalUniqueIds + matchingIDs.size >= allowedUniqueBasketQty
    },

    isAgeRestrictedProductAllowed: product => {
        const { configuration } = store.getState()
        const ageRestricted = productUtility.isAgeRestrictedProduct(product)
        const { enabled_age_restricted_products_web } = configuration.toJS()
        const ageRestrictedAllowed = enabled_age_restricted_products_web
        return !ageRestrictedAllowed && ageRestricted ? false : true
    },

    isAgeRestrictedProduct: product => {
        if (product && product.age_restricted === true) {
            return true
        }
        return false
    },

    isProductAvailable: product => {
        const { count } = productUtility.productExistsInCart(product)

        if (!product.available) {
            return { itemAvailable: false, error: strings.itemUnavailable }
        }

        // if (product.available && count >= product.limit_per_order ) {
        //     return { itemAvailable: false, error: strings.maxQtyAdded }
        // }
        let availableInventory
        if (product.limit_per_order >= 0 && product.availableQuantity >= 0) {
            availableInventory =
                product.limit_per_order < product.availableQuantity
                    ? product.limit_per_order
                    : product.availableQuantity
        } else {
            availableInventory = product.limit_per_order
        }

        if (product.available && count < availableInventory) {
            return { itemAvailable: true, error: '' }
        } else {
            if (product.available && count > 0 && count >= availableInventory) {
                return { itemAvailable: false, error: strings.maxQtyAdded }
            }

            return { itemAvailable: false, error: strings.itemUnavailable }
        }
    },

    getBasketTotals: () => {
        const { basket } = store.getState()
        const localBasket = basket.toJS()
        const { basketItems } = localBasket

        let total = 0
        let totalCount = 0
        const totalUniqueCount = new Set()

        if (basketItems && basketItems.length > 0) {
            basketItems.forEach(item => {
                total += productUtility.getPrice(item) * item.count
                totalCount += item.count

                if (item.matching_ids && item.matching_ids.length > 0) {
                    item.matching_ids.forEach(id => {
                        totalUniqueCount.add(id)
                    })
                }
            })
        }

        return {
            basketAmount: total,
            basketCount: totalCount,
            totalUniqueIds: totalUniqueCount.size,
        }
    },

    productExistsInCart: (product, items = null) => {
        let basketItems
        if (items === null) {
            const { basket } = store.getState()
            const localBasket = basket ? basket.toJS().basketItems : []
            basketItems = localBasket
        } else {
            basketItems = items
        }

        if (basketItems && basketItems.length > 0) {
            const productFound = basketItems.find(item => item.product_id === product.product_id)
            if (productFound) {
                return {
                    exists: true,
                    count: productFound.count,
                }
            }
        }
        return {
            exists: false,
            count: 0,
        }
    },

    getPrice: product => {
        if (product && product.price) {
            if (product.promo_price && product.promo_price < product.price)
                return product.promo_price
            return product.price
        }
        return 0
    },

    subtractAndFormat: (a, b) => {
        const res = a - b
        return `$${(res / 100).toFixed(2)}`
    },

    formatPrice: amount => `$${(amount / 100).toFixed(2)}`,

    getTotalItems: items => {
        const basketItems = items.toJS()
        if (basketItems.length <= 0) return 0

        let totalCount = 0
        for (let loop = 0; loop < basketItems.length; loop++) {
            const item = basketItems[loop]
            if (item.count > 0) {
                totalCount += item.count
            }
        }

        // const totalCount = basketItems.reduce((total, currentItem) => {
        //     if (currentItem.count > 0) {
        //         total += currentItem.count
        //     }
        // }, 0)

        return totalCount
    },

    isTobaccoOrCbdProduct: tags => {
        const restrictedProducts = ['tobacco', 'CBD']
        const checker = value => restrictedProducts.some(element => value.includes(element))
        return !!(tags && tags.some(checker))
    },

    ageVerificationNeeded: basketItems => {
        if (basketItems && basketItems.length > 0) {
            const ageRestrictedItems = basketItems.filter(item => item.age_restricted)
            const ageTobeVerified = ageRestrictedItems.reduce(
                (prev, current) => {
                    if (!current.age_restriction) return prev
                    return prev.age_restriction > current.age_restriction ? prev : current
                },
                { age_restriction: 0 }
            ).age_restriction

            return {
                isNeeded: ageRestrictedItems.length > 0,
                age: ageTobeVerified || 21,
            }
        }
        return { isNeeded: false, age: 0 }
    },
}
